import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useTree } from '@/hooks/useTree';
import { geocodePlaces, type GeoResult } from '@/utils/geocode';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

// ─── FogLayer ────────────────────────────────────────────────────────────────

function FogLayer({ locations, fogEnabled }: { locations: [number, number][]; fogEnabled: boolean }) {
  const map = useMap();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    // Cleanup previous canvas
    canvasRef.current?.remove();
    canvasRef.current = null;

    if (!fogEnabled) return;

    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;top:0;left:0;pointer-events:none;z-index:400;';
    map.getPanes().overlayPane.appendChild(canvas);
    canvasRef.current = canvas;

    const draw = () => {
      const size = map.getSize();
      canvas.width  = size.x;
      canvas.height = size.y;
      canvas.style.width  = size.x + 'px';
      canvas.style.height = size.y + 'px';

      const topLeft = map.containerPointToLayerPoint([0, 0]);
      canvas.style.transform = `translate(${topLeft.x}px,${topLeft.y}px)`;

      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, size.x, size.y);

      // Dark fog
      ctx.fillStyle = 'rgba(15,23,42,0.82)';
      ctx.fillRect(0, 0, size.x, size.y);

      // Cut revealed circles
      ctx.globalCompositeOperation = 'destination-out';
      for (const [lat, lng] of locations) {
        const pt = map.latLngToContainerPoint([lat, lng]);
        const zoom = map.getZoom();
        const radius = Math.max(50, 15 * Math.pow(2, zoom - 4));
        const grad = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, radius);
        grad.addColorStop(0,    'rgba(0,0,0,1)');
        grad.addColorStop(0.6,  'rgba(0,0,0,0.95)');
        grad.addColorStop(0.85, 'rgba(0,0,0,0.4)');
        grad.addColorStop(1,    'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
    };

    draw();
    map.on('moveend zoomend resize move zoom', draw);

    return () => {
      map.off('moveend zoomend resize move zoom', draw);
      canvas.remove();
      canvasRef.current = null;
    };
  }, [map, locations, fogEnabled]);

  return null;
}

// ─── FogOfWarPage ────────────────────────────────────────────────────────────

export function FogOfWarPage() {
  const { data: treeData, isLoading } = useTree();
  const [geoMap, setGeoMap] = useState<Map<string, GeoResult>>(new Map());
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [fogEnabled, setFogEnabled] = useState(true);

  // Extract all places (same pattern as MapView.tsx)
  const allPlaces = useMemo(() => {
    if (!treeData) return [];
    const places = new Set<string>();

    for (const node of treeData.nodes) {
      if (node.birthPlace) places.add(node.birthPlace);
      if (node.deathPlace) places.add(node.deathPlace);
    }
    for (const link of treeData.links) {
      if (link.startPlace) places.add(link.startPlace);
    }

    return [...places];
  }, [treeData]);

  // Geocode (same pattern as MapView.tsx)
  useEffect(() => {
    if (allPlaces.length === 0) return;

    let cancelled = false;
    setIsGeocoding(true);

    geocodePlaces(allPlaces).then((results) => {
      if (!cancelled) {
        setGeoMap(results);
        setIsGeocoding(false);
      }
    });

    return () => { cancelled = true; };
  }, [allPlaces]);

  // Discovered locations as [lat, lng][]
  const discoveredLocations = useMemo(
    () => [...geoMap.values()].map((g): [number, number] => [g.lat, g.lng]),
    [geoMap],
  );

  const coveragePct = Math.min(100, Math.round((geoMap.size / Math.max(allPlaces.length, 1)) * 100));
  const xpEarned = geoMap.size * 25;
  const rank =
    xpEarned < 200  ? 'Entdecker' :
    xpEarned < 500  ? 'Erkunder' :
    xpEarned < 1000 ? 'Kartograph' :
                      'Meister-Kartograph';

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex h-full items-center justify-center">
          <LoadingSpinner size="lg" message="Stammbaum wird geladen..." />
        </div>
      </AppLayout>
    );
  }

  if (!treeData || allPlaces.length === 0) {
    return (
      <AppLayout>
        <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-500">
          <span className="text-5xl">🗺️</span>
          <p className="text-lg font-semibold text-slate-700">Keine Ortsdaten vorhanden</p>
          <p className="text-sm">Füge Geburts-, Wohn- oder Sterbeorte hinzu, um die Karte zu erkunden!</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* isolate: eigener Stacking-Context → Leaflets hohe Pane-z-indexe (bis 700)
         bleiben lokal und können die globale Topbar-Navigation nicht überdecken. */}
      <div className="relative isolate h-full w-full overflow-hidden">

        {/* Stats panel — top center */}
        <div
          className="absolute top-4 left-1/2 z-[500] -translate-x-1/2 rounded-2xl border border-slate-200 bg-white/95 px-5 py-3 shadow-xl backdrop-blur-sm"
          style={{ minWidth: 300 }}
        >
          <p className="mb-1 text-center text-[10px] font-semibold uppercase tracking-widest text-slate-400">
            Entdecker-Karte · {rank}
          </p>
          <div className="flex items-center justify-center gap-5">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-800">{geoMap.size}</p>
              <p className="text-[10px] text-slate-500">Orte entdeckt</p>
            </div>
            <div className="h-8 w-px bg-slate-200" />
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-500">{xpEarned} XP</p>
              <p className="text-[10px] text-slate-500">verdient</p>
            </div>
            <div className="h-8 w-px bg-slate-200" />
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{coveragePct}%</p>
              <p className="text-[10px] text-slate-500">geocodiert</p>
            </div>
          </div>
          {/* XP bar */}
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-300 transition-all duration-700"
              style={{ width: `${coveragePct}%` }}
            />
          </div>
        </div>

        {/* Fog toggle — top left */}
        <div className="absolute left-4 top-4 z-[500]">
          <button
            onClick={() => setFogEnabled((v) => !v)}
            className={[
              'flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium shadow-lg backdrop-blur-sm transition-all',
              fogEnabled
                ? 'border-slate-600 bg-slate-800/90 text-white hover:bg-slate-700'
                : 'border-slate-200 bg-white/95 text-slate-700 hover:bg-slate-50',
            ].join(' ')}
          >
            <span className="text-lg">{fogEnabled ? '🌫️' : '☀️'}</span>
            {fogEnabled ? 'Nebel aktiv' : 'Karte vollständig'}
          </button>
        </div>

        {/* Geocoding progress */}
        {isGeocoding && (
          <div className="absolute bottom-6 left-1/2 z-[500] -translate-x-1/2 rounded-lg border border-slate-200 bg-white px-4 py-2 shadow">
            <p className="text-sm text-slate-600">
              Orte werden gesucht… ({geoMap.size}/{allPlaces.length})
            </p>
          </div>
        )}

        {/* Map */}
        <MapContainer
          center={[50.5, 10.5]}
          zoom={6}
          className="h-full w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FogLayer locations={discoveredLocations} fogEnabled={fogEnabled} />
        </MapContainer>
      </div>
    </AppLayout>
  );
}
